import { useEffect } from 'react';
import {
  useSelectedOrganizationId,
  useAuthtoken,
  logout,
} from 'stores/useAuthStore';
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';

import request from 'utils/request';
import { setAppLoading, showSnackbar } from 'stores/useUiStore';
import type { ApiRespBody } from 'types/api';
import type { IpRange, Location } from './types';


const TOKEN_INVALID = 'Token is not valid';

export const LOCATIONS_QUERY_KEY = ['locationManagement'] as const;

function authHeaders(authToken: string) {
  return {
    'Content-Type': 'application/json',
    authorization: authToken,
  };
}

async function fetchLocations(authToken: string): Promise<Location[]> {
  const result = await request<ApiRespBody<Location[]>>(
    '/api/devices/testingLocations',
    { method: 'GET', headers: { authorization: authToken } }
  );
  if (!result?.success || !result.data) {
    if (result?.message === TOKEN_INVALID) throw new Error(TOKEN_INVALID);
    throw new Error(result?.message || 'Error loading locations');
  }
  return result.data;
}

export function locationsQueryOptions(
  authToken: string,
  organizationId: number
) {
  return queryOptions({
    queryKey: [...LOCATIONS_QUERY_KEY, organizationId],
    queryFn: () => fetchLocations(authToken),
  });
}

function useLogoutOnInvalidToken(error: unknown) {
  useEffect(() => {
    if (error instanceof Error && error.message === TOKEN_INVALID) {
      logout();
    }
  }, [error]);
}

export function useLocationsQuery() {
  const authToken = useAuthtoken();
  const organizationId = useSelectedOrganizationId();
  const query = useQuery(locationsQueryOptions(authToken, organizationId));
  useLogoutOnInvalidToken(query.error);
  return query;
}

export function useLocationManagementMutations() {
  const authToken = useAuthtoken();
  const queryClient = useQueryClient();
  const headers = authHeaders(authToken);

  function mutationOptions<TVars>(cfg: {
    label: string;
    caller: string;
    successMessage: string | ((vars: TVars) => string);
    run: (vars: TVars) => Promise<ApiRespBody | null>;
  }): UseMutationOptions<void, Error, TVars> {
    return {
      mutationFn: async (vars: TVars) => {
        setAppLoading({ isLoading: true, label: cfg.label });
        const result = await cfg.run(vars);
        if (!result?.success) {
          throw new Error(result?.message || `${cfg.caller} failed`);
        }
      },
      onSuccess: (_data, vars) => {
        showSnackbar({
            autoHideDuration: 5000,
            caller: cfg.caller,
            open: true,
            message:
              typeof cfg.successMessage === 'function'
                ? cfg.successMessage(vars)
                : cfg.successMessage,
            severity: 'success',
          });
        queryClient.invalidateQueries({ queryKey: LOCATIONS_QUERY_KEY });
      },
      onError: (error) => {
        if (error.message === TOKEN_INVALID) {
          logout();
          return;
        }
        showSnackbar({
            autoHideDuration: null,
            caller: cfg.caller,
            open: true,
            message: error.message,
            severity: 'error',
          });
      },
      onSettled: () => {
        setAppLoading({ isLoading: false, label: '' });
      },
    };
  }

  const saveLocationMutation = useMutation(
    mutationOptions<Partial<Location>>({
      label: 'Saving Location',
      caller: 'onLocationSave',
      successMessage: (location) =>
        location.id ? 'Location updated' : 'Location added',
      run: (location) => {
        const isNew = !location.id;
        return request<ApiRespBody>(
          isNew
            ? '/api/devices/testingLocations'
            : `/api/devices/testingLocations/${location.id}`,
          {
            method: isNew ? 'POST' : 'PUT',
            body: JSON.stringify(location),
            headers,
          }
        );
      },
    })
  );

  const deleteLocationMutation = useMutation(
    mutationOptions<number>({
      label: 'Deleting Location',
      caller: 'onLocationDelete',
      successMessage: 'Location deleted',
      run: (locationId) =>
        request<ApiRespBody>(
          `/api/devices/testingLocations/${locationId}`,
          { method: 'DELETE', headers: { authorization: authToken } }
        ),
    })
  );

  const saveIpRangeMutation = useMutation(
    mutationOptions<{ locationId: number; ipRange: Partial<IpRange> }>({
      label: 'Saving IP Range',
      caller: 'onIpRangeSave',
      successMessage: ({ ipRange }) =>
        ipRange.id ? 'IP range updated' : 'IP range added',
      run: ({ locationId, ipRange }) => {
        const isNew = !ipRange.id;
        return request<ApiRespBody>(
          isNew
            ? `/api/devices/testingLocations/${locationId}/ipRanges`
            : `/api/devices/testingLocations/ipRanges/${ipRange.id}`,
          {
            method: isNew ? 'POST' : 'PUT',
            body: JSON.stringify(ipRange),
            headers,
          }
        );
      },
    })
  );

  const deleteIpRangeMutation = useMutation(
    mutationOptions<number>({
      label: 'Deleting IP Range',
      caller: 'onIpRangeDelete',
      successMessage: 'IP range deleted',
      run: (ipRangeId) =>
        request<ApiRespBody>(
          `/api/devices/testingLocations/ipRanges/${ipRangeId}`,
          { method: 'DELETE', headers: { authorization: authToken } }
        ),
    })
  );

  return {
    saveLocation: (location: Partial<Location>) =>
      saveLocationMutation.mutate(location),
    deleteLocation: (locationId: number) =>
      deleteLocationMutation.mutate(locationId),
    saveIpRange: (locationId: number, ipRange: Partial<IpRange>) =>
      saveIpRangeMutation.mutate({ locationId, ipRange }),
    deleteIpRange: (ipRangeId: number) =>
      deleteIpRangeMutation.mutate(ipRangeId),
  };
}
